import i18next, { type Resource, type i18n as I18nInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';

import authDe from './resources/de/auth.json';
import commonDe from './resources/de/common.json';
import timerDe from './resources/de/timer.json';
import authEn from './resources/en/auth.json';
import commonEn from './resources/en/common.json';
import timerEn from './resources/en/timer.json';
import authEs from './resources/es/auth.json';
import commonEs from './resources/es/common.json';
import timerEs from './resources/es/timer.json';
import authFr from './resources/fr/auth.json';
import commonFr from './resources/fr/common.json';
import timerFr from './resources/fr/timer.json';
import authIt from './resources/it/auth.json';
import commonIt from './resources/it/common.json';
import timerIt from './resources/it/timer.json';

export const defaultNamespace = 'common';

/** ISO 639-1 codes for every language this product ships a translation for. English is the fallback. */
export const SUPPORTED_LANGUAGES = ['en', 'it', 'de', 'fr', 'es'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/** Native-script display name for each supported language, for use in language switchers. */
export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  en: 'English',
  it: 'Italiano',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
};

/**
 * Languages that read right-to-left. Drives the `dir` attribute each app sets
 * on its root element. Empty today — Arabic was the only RTL language and is
 * no longer shipped — but the direction plumbing (`getTextDirection`, the
 * `dir`/`I18nManager` calls below and in mobile) is kept so adding an RTL
 * language back is a one-entry change rather than a re-implementation.
 */
export const RTL_LANGUAGES: ReadonlySet<SupportedLanguage> = new Set([]);

export function getTextDirection(language: SupportedLanguage): 'ltr' | 'rtl' {
  return RTL_LANGUAGES.has(language) ? 'rtl' : 'ltr';
}

export function isSupportedLanguage(value: string): value is SupportedLanguage {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

const LANGUAGE_STORAGE_KEY = 'workingclock:language';

/**
 * Reads a persisted web language choice, falling back to the browser's
 * locale, then English. No-ops to 'en' outside a browser (SSR/mobile) —
 * mobile detects/persists its own choice instead (SecureStore has no sync
 * read, so it passes `lng` explicitly and hydrates later; see
 * apps/mobile/src/stores/language-store.ts).
 */
export function readStoredLanguage(): SupportedLanguage {
  if (typeof window === 'undefined') return 'en';
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  } catch {
    // localStorage can throw (privacy mode, blocked site data, or absent in
    // some test/SSR shells) — treat as "no stored choice" so the browser
    // locale below still applies.
  }
  if (stored && isSupportedLanguage(stored)) return stored;
  // `window` existing does not imply a browser: React Native's jest-expo
  // environment defines a `window` global with no `navigator` on it, so this
  // has to be optional all the way down rather than guarded by `typeof window`
  // alone.
  const browserLang = window.navigator?.language?.split('-')[0];
  return browserLang && isSupportedLanguage(browserLang) ? browserLang : 'en';
}

/** Persists a web language choice. No-ops outside a browser (or where localStorage is unavailable/throws). */
export function persistLanguage(language: SupportedLanguage): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // Ignore — same rationale as readStoredLanguage's catch.
  }
}

const sharedResourcesByLanguage: Record<SupportedLanguage, { common: object; auth: object; timer: object }> = {
  en: { common: commonEn, auth: authEn, timer: timerEn },
  it: { common: commonIt, auth: authIt, timer: timerIt },
  de: { common: commonDe, auth: authDe, timer: timerDe },
  fr: { common: commonFr, auth: authFr, timer: timerFr },
  es: { common: commonEs, auth: authEs, timer: timerEs },
};

/** Back-compat export for anything still importing the English bundle directly. */
export const enResources = sharedResourcesByLanguage.en;

export interface CreateI18nOptions {
  /**
   * App-specific namespaces merged on top of the shared resources, keyed by
   * language (e.g. `{ en: { webapp: webappEn }, it: { webapp: webappIt } }`).
   * A language missing an entry here still gets the shared common/auth/timer
   * namespaces — only that app's own namespace falls back to i18next's
   * `fallbackLng` resolution (English) for untranslated strings.
   */
  extraResources?: Partial<Record<SupportedLanguage, Record<string, object>>>;
  debug?: boolean;
  /**
   * Overrides the initial language. Web apps default to `readStoredLanguage()`
   * (localStorage, then browser locale). Mobile has no synchronous storage
   * read, so it passes its own detected/persisted value here instead.
   */
  lng?: SupportedLanguage;
}

export function createI18n(options: CreateI18nOptions = {}): I18nInstance {
  const instance = i18next.createInstance();
  const resources: Resource = {};
  for (const lang of SUPPORTED_LANGUAGES) {
    resources[lang] = { ...sharedResourcesByLanguage[lang], ...options.extraResources?.[lang] };
  }

  const initialLanguage = options.lng ?? readStoredLanguage();

  void instance.use(initReactI18next).init({
    resources,
    lng: initialLanguage,
    fallbackLng: 'en',
    defaultNS: defaultNamespace,
    interpolation: { escapeValue: false },
    debug: options.debug ?? false,
  });

  applyDocumentDirection(initialLanguage);

  instance.on('languageChanged', (lng) => {
    if (!isSupportedLanguage(lng)) return;
    persistLanguage(lng);
    applyDocumentDirection(lng);
  });

  return instance;
}

/**
 * Syncs `dir`/`lang` on the document root for every browser-based consumer
 * (webapp, desktop's Tauri webview, marketing). Mobile has no `document` —
 * it drives RN's own `I18nManager.forceRTL` from `getTextDirection()`
 * instead (see apps/mobile/src/stores/language-store.ts), so this no-ops
 * there via the `typeof document` guard.
 */
function applyDocumentDirection(language: SupportedLanguage): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dir = getTextDirection(language);
  document.documentElement.lang = language;
}

/**
 * Memoized `Intl.DateTimeFormat`, keyed by locale + options.
 *
 * Every date/time label in the apps used to be produced by a module-level
 * `new Intl.DateTimeFormat('en-US', ...)` constant. Hoisting it was the right
 * instinct — constructing a formatter is expensive and these run once per
 * table row or grid cell — but a module-level constant can only ever hold one
 * locale, so the whole product rendered its dates in English no matter which
 * language the user picked. This keeps the hoisting and moves the locale into
 * the cache key, so call sites can pass `i18n.language` per call and still pay
 * for construction only once per (locale, format) pair.
 *
 * `options` is stringified for the key, so pass a stable object (a
 * module-level constant) rather than building a fresh literal per call.
 */
const dateTimeFormatCache = new Map<string, Intl.DateTimeFormat>();

export function getDateTimeFormat(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale}\u0000${JSON.stringify(options)}`;
  let formatter = dateTimeFormatCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options);
    dateTimeFormatCache.set(key, formatter);
  }
  return formatter;
}

export type { TFunction } from 'i18next';
